import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Globe, Users, Trophy, ShieldCheck } from "@phosphor-icons/react"

export function InternationalPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-ivory via-ivory to-muted">
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/20 text-navy text-sm font-medium uppercase tracking-wider mb-6">
              ⚽ Global Football Pathways
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-semibold text-navy mb-6">
              International Football Exposure & Pathways
            </h1>
            <p className="text-xl text-navy-muted max-w-2xl mx-auto">
              Connecting young footballers from Asia and Africa to the world's elite football ecosystems
            </p>
          </div>
          
          {/* Image 3: Environmental context */}
          <div className="aspect-[3/1] overflow-hidden rounded-lg mb-16">
            <img 
              src="/images/international-pathways.jpg" 
              alt="" 
              className="w-full h-full object-cover object-top"
              aria-hidden="true"
            />
          </div>

          <div className="space-y-12">
            <section>
              <div className="flex items-center gap-3 mb-6">
                <Globe className="text-gold" size={32} weight="duotone" />
                <h2 className="font-serif text-3xl font-semibold text-navy">Global Vision</h2>
              </div>
              <Card className="border-gold/20">
                <CardContent className="pt-6">
                  <p className="text-lg text-navy-muted leading-relaxed">
                    ZOE Football Academy connects young footballers from Asia and Africa to global football ecosystems, 
                    creating opportunities for growth, exposure, and professional football advancement.
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <img src="/images/gallery-1.jpg" alt="Football training" className="rounded-lg" />
                    <img src="/images/international-pathways.jpg" alt="Football matches" className="rounded-lg" />
                    <img src="/images/gallery-3.jpg" alt="Global opportunities" className="rounded-lg" />
                  </div>
                </CardContent>
              </Card>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <Users className="text-gold" size={32} weight="duotone" />
                <h2 className="font-serif text-3xl font-semibold text-navy">Exchange Programs</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: "India ↔ Africa Exchange",
                    description: "Annual exchange camps building cultural bridges and competitive experience"
                  },
                  {
                    title: "International Academies",
                    description: "Training partnerships with elite academies worldwide"
                  },
                  {
                    title: "Cultural Exposure",
                    description: "Competitive and cultural growth through international experience"
                  }
                ].map((item, idx) => (
                  <Card key={idx} className="border-gold/20 hover:border-gold/40 transition-colors">
                    <CardHeader>
                      <CardTitle className="text-lg text-navy">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-navy-muted text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <Trophy className="text-gold" size={32} weight="duotone" />
                <h2 className="font-serif text-3xl font-semibold text-navy">Tournaments & Showcases</h2>
              </div>
              <Card className="border-gold/20">
                <CardContent className="pt-6">
                  <ul className="space-y-4">
                    {[
                      {
                        title: "International Youth Tournaments",
                        description: "High-level competitive exposure against international talent"
                      },
                      {
                        title: "Scouting Combines",
                        description: "Structured evaluation events with professional scouts and clubs"
                      },
                      {
                        title: "Invitational Events",
                        description: "Exclusive showcase opportunities for top performers"
                      }
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4">
                        <div className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-navy mb-1">{item.title}</h4>
                          <p className="text-navy-muted text-sm">{item.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </section>

            <section>
              <h2 className="font-serif text-3xl font-semibold text-navy mb-6">
                Placement Pathways
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: "Professional Clubs",
                    regions: "Africa, Asia, Europe",
                    description: "Direct pathways to professional club systems"
                  },
                  {
                    title: "Academy-to-Club",
                    regions: "Global Network",
                    description: "Structured progression from academy to professional level"
                  },
                  {
                    title: "Scholarships",
                    regions: "USA, Europe, Asia",
                    description: "Academic and athletic scholarship opportunities"
                  }
                ].map((item, idx) => (
                  <Card key={idx} className="border-gold/20 hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg text-navy">{item.title}</CardTitle>
                      <div className="text-xs text-gold font-medium uppercase tracking-wider">
                        {item.regions}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-navy-muted text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <div className="flex items-center gap-3 mb-6">
                <ShieldCheck className="text-gold" size={32} weight="duotone" />
                <h2 className="font-serif text-3xl font-semibold text-navy">Ethics & Compliance</h2>
              </div>
              <Card className="border-gold/20 bg-gradient-to-br from-gold/5 to-gold/10">
                <CardContent className="pt-6">
                  <p className="text-lg text-navy-muted leading-relaxed mb-4">
                    All international football movement follows FIFA youth transfer regulations, with strict welfare 
                    and parental safeguards.
                  </p>
                  <p className="text-xl font-semibold text-navy">
                    ZOE Football Academy prioritizes ethical opportunity over exploitation.
                  </p>
                </CardContent>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
